import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import {
  isSocialLoginSuccess,
  socialLoginCreateAccount,
  socialLoginRequestVerificationCode
} from '../data/repositories/SocialLogin';
import { completeAuthLogin } from '../utils/completeAuthLogin';
import type { SocialProvider } from '../data/repositories/SocialLoginMethods';
import type { SocialCandidateEmail } from '../data/repositories/SocialLogin';

const PROVIDER_DISPLAY_NAMES: Record<SocialProvider, string> = {
  google: 'Google',
  kakao: 'Kakao',
  apple: 'Apple',
  facebook: 'Facebook'
};

interface SocialAccountSelectionPageState {
  provider?: SocialProvider;
  socialSessionToken?: string;
  candidateEmails?: SocialCandidateEmail[];
  continueWithNewAccountAvailable?: boolean;
  isPromotionAgreed?: boolean;
}

export default function SocialAccountSelectionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as SocialAccountSelectionPageState | null) ?? null;

  const provider = state?.provider ?? 'google';
  const socialSessionToken = state?.socialSessionToken || '';
  const candidateEmails = Array.isArray(state?.candidateEmails) ? state.candidateEmails : [];
  const continueWithNewAccountAvailable = state?.continueWithNewAccountAvailable === true;
  const isPromotionAgreed = state?.isPromotionAgreed === true;
  const methodDisplayName = PROVIDER_DISPLAY_NAMES[provider] ?? PROVIDER_DISPLAY_NAMES.google;

  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);

  const isLoginButtonDisabled = !selectedEmail;

  const onBackClick = () => {
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    navigate('/onboarding', { replace: true, state: { skipToLastStep: true } });
  };

  const handleSelectCandidateEmail = async (selectedEmail: string) => {
    if (!socialSessionToken) return;

    try {
      const verificationResponse = await socialLoginRequestVerificationCode({
        socialSessionToken,
        selectedEmail
      });

      if (verificationResponse.success) {
        navigate('/login-with-code?init=true', {
          state: {
            email: verificationResponse.email || selectedEmail,
            maskedEmail: verificationResponse.maskedEmail,
            socialSessionToken,
            socialSelectedEmail: selectedEmail,
            isPromotionAgreed
          }
        });
        return;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateNewAccount = async () => {
    if (!socialSessionToken) return;

    setIsCreatingAccount(true);

    try {
      const response = await socialLoginCreateAccount({
        socialSessionToken,
        isPromotionAgreed
      });

      if (isSocialLoginSuccess(response)) {
        const completed = completeAuthLogin({ response, navigate });
        if (!completed) {
          console.error('Failed to complete auth login');
        }
        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  if (!socialSessionToken || candidateEmails.length === 0) {
    return (
      <div className="flex flex-col h-screen items-center justify-center px-[24px]">
        <span className="text-[15px] text-text-main-lv4 text-center whitespace-pre-line">
          {t('requestVerificationCodePage.socialCandidateEmailsMissingMessage')}
        </span>
        <button
          type="button"
          className="mt-[20px] underline text-[14px]"
          onClick={() => navigate('/onboarding', { replace: true, state: { skipToLastStep: true } })}
        >
          {t('requestVerificationCodePage.socialExpandMethods')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark items-center w-[100vw] max-w-[570px] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <button
        className="mt-[14px] px-[16px] mb-[15px] w-full z-50 justify-start"
        onClick={onBackClick}
        type="button"
      >
        <Xmark size={24} className="text-text-main-lv1" />
      </button>

      <div className="w-full max-w-[570px] px-[24px] flex flex-col">
        <div className="flex flex-col items-center gap-[14px] py-[10px] mt-[90px]">
          <span className="text-center text-[26px] font-bold leading-[31px] text-text-main-lv1">
            {t('requestVerificationCodePage.socialSelectCandidateTitle')}
          </span>
          <span className="text-text-main-lv4 text-[15px] text-center font-semibold leading-[18px] whitespace-pre-line">
            {t('requestVerificationCodePage.socialSelectCandidateDescription', {
              method: methodDisplayName
            })}
          </span>
        </div>
        <div className="flex flex-col gap-[14px] px-[27px] mt-[29px]">
          {candidateEmails.map((candidate) => {
            return (
              <button
                key={candidate.email}
                type="button"
                onClick={() => setSelectedEmail(candidate.email)}
                className={`
                flex items-center justify-center
                rounded-[12px]
                min-h-[42px]
                bg-separator1
                text-text-main-lv2
                text-[15px] font-semibold
                ${selectedEmail === candidate.email ? 'border border-primary-light dark:border-primary-dark' : ''}
              `}
              >
                <span>{candidate.maskedEmail}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 pb-[calc(39px+env(safe-area-inset-bottom))] w-full max-w-[570px] px-[24px]">
        <Button
          size="large"
          onClick={() => handleSelectCandidateEmail(selectedEmail || '')}
          disabled={isLoginButtonDisabled}
          className="text-[17px] font-semibold w-full"
        >
          {t('requestVerificationCodePage.getVerificationCode')}
        </Button>
        {continueWithNewAccountAvailable ? (
          <Button
            type="button"
            size="large"
            btnColor="secondary"
            onClick={handleCreateNewAccount}
            disabled={isCreatingAccount}
            className="text-[17px] font-semibold w-full mt-[9px]"
          >
            {t('requestVerificationCodePage.socialContinueWithNewAccount')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
